import React, { useState } from "react";
import FormUI from '@/componentsUI/Form';
import PopupMask from "@/componentsUI/PopupMask";
import ButtonUI from "@/componentsUI/Button";
import InputUI from "@/componentsUI/Input";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import { feedback } from "@/utils/feedback";
import { WITHDRAW_EMAIL_MAX_LENGTH } from "../../constants";
import styles from "./index.module.less";

export interface WithdrawConfirmModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: { fullName: string; email?: string }) => void;
  withdrawData?: any;
  loading?: boolean;
  requireEmail?: boolean;
}

const WithdrawConfirmModal: React.FC<WithdrawConfirmModalProps> = ({
  visible,
  onClose,
  onConfirm,
  loading = false,
  requireEmail = false,
}) => {
  const { t } = useTranslationPlus("Wallet");
  const [form] = FormUI.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    form
      .validate()
      .then((values) => {
        setIsSubmitting(true);
        const confirmData = {
          fullName: values.fullName,
          email: values.email,
        };
        onConfirm(confirmData);
      })
      .catch((error) => {
        console.log("Validation failed:", error);
        feedback.error(t("Please fill in all required fields"));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    setIsSubmitting(false);
    onClose();
  };

  if (!visible) return null;

  return (
    <PopupMask visible={visible} onClose={handleCancel}>
      <div className={styles["withdraw-confirm-modal"]}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t("Confirm Withdrawal")}</h3>
          <button className={styles.closeBtn} onClick={handleCancel}>
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.infoSection}>
            <p className={styles.infoText}>
              {t(
                "Please provide the following information to complete your withdrawal:"
              )}
            </p>
          </div>

          <div className={styles.formSection}>
            <FormUI form={form} layout="vertical" autoComplete="off">
              <FromItemPlus
                field="fullName"
                label={t("Full Name")}
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: t("Please enter your full name"),
                  },
                  {
                    minLength: 2,
                    message: t("Full name must be at least 2 characters"),
                  },
                ]}
              >
                <InputUI
                  placeholder={t("Enter your full name")}
                  className={styles.formInput}
                />
              </FromItemPlus>

              <FromItemPlus
                field="email"
                label={
                  requireEmail
                    ? t("Email Address")
                    : t("Email Address") + " (" + t("Optional") + ")"
                }
                className={styles.formItem}
                rules={[
                  {
                    required: requireEmail,
                    message: t("Please enter your email address"),
                  },
                  {
                    type: "email",
                    message: t("Please enter a valid email address"),
                    validator: (value, callback) => {
                      if (value && value.trim() !== "") {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(value)) {
                          return callback(
                            t("Please enter a valid email address")
                          );
                        }
                      }
                      return callback();
                    },
                  },
                ]}
              >
                <InputUI
                  placeholder={
                    requireEmail
                      ? t("Enter your email address")
                      : t("Enter your email address") +
                        " (" +
                        t("Optional") +
                        ")"
                  }
                  maxLength={WITHDRAW_EMAIL_MAX_LENGTH}
                  className={styles.formInput}
                />
              </FromItemPlus>
            </FormUI>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <ButtonUI className={styles.cancelBtn} onClick={handleCancel}>
            {t("Cancel")}
          </ButtonUI>
          <ButtonUI
            type="primary"
            className={styles.confirmBtn}
            onClick={handleConfirm}
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {t("Confirm Withdrawal")}
          </ButtonUI>
        </div>
      </div>
    </PopupMask>
  );
};

export default WithdrawConfirmModal;
